import React, { useState, useRef } from 'react';
import styles from './CategorySelectionMessage.scss';
import { CategoryService } from '../../utils/filters';

// UI Constants for selection indicators
const SELECTION_INDICATORS = {
  CHECKED: '☑',
  UNCHECKED: '☐',
  SELECTED_RADIO: '●',
  UNSELECTED_RADIO: '○',
  SEARCH_ICON: '🔍',
};

const CategorySelectionMessage = ({ message, onSubmit, isLightMode }) => {
  const { selectionPayload, submitted, selectedValues = [] } = message;
  const [selected, setSelected] = useState(selectedValues);
  // Prevents double-submit between click and Redux state propagation
  const submittingRef = useRef(false);

  if (!selectionPayload) return null;

  const { options = [], multi_select: multiSelect, message: promptText } = selectionPayload;

  const handleOptionClick = (value) => {
    if (submitted || submittingRef.current) return;
    if (!multiSelect) {
      submittingRef.current = true;
      // Single-select: submit immediately on click
      onSubmit([value], message);
    } else {
      setSelected(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    }
  };

  const handleConfirm = () => {
    if (selected.length > 0 && !submittingRef.current) {
      submittingRef.current = true;
      onSubmit(selected, message);
    }
  };

  // When submitted, show which values were chosen
  const submittedLabels = CategoryService.getSelectedCategoryLabels(message.selectedValues, options);

  return (
    <div className={`${styles.wrapper} ${isLightMode ? styles.lightMode : ''} ${submitted ? styles.submitted : ''}`}>
      <div className={styles.header}>
        <span className={styles.icon}>{SELECTION_INDICATORS.SEARCH_ICON}</span>
        <span className={styles.prompt}>{promptText}</span>
      </div>

      <div className={styles.optionsList}>
        {options.map(option => {
          const isSelected = selected.includes(option.value) ||
            (submitted && (message.selectedValues || []).includes(option.value));
          return (
            <div
              key={option.value}
              className={`${styles.option}
                ${isSelected ? styles.selected : ''}
                ${submitted ? styles.disabled : ''}
                ${!multiSelect ? styles.radio : styles.checkbox}`}
              onClick={() => handleOptionClick(option.value)}
            >
              <span className={styles.indicator}>
                {multiSelect
                  ? (isSelected ? SELECTION_INDICATORS.CHECKED : SELECTION_INDICATORS.UNCHECKED)
                  : (isSelected ? SELECTION_INDICATORS.SELECTED_RADIO : SELECTION_INDICATORS.UNSELECTED_RADIO)}
              </span>
              <div className={styles.optionText}>
                <span className={styles.optionName}>{option.display_name}</span>
                {option.breadcrumb && (
                  <span className={styles.optionBreadcrumb}>{option.breadcrumb}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {multiSelect && !submitted && (
        <button
          className={styles.confirmButton}
          onClick={handleConfirm}
          disabled={selected.length === 0}
        >
          {selected.length > 0 ? `Confirm (${selected.length} selected)` : 'Confirm Selection'}
        </button>
      )}

      {submitted && submittedLabels.length > 0 && (
        <div className={styles.submittedBadge}>
          ✓ {submittedLabels.join(', ')}
        </div>
      )}
    </div>
  );
};

export default CategorySelectionMessage;
